'use client';

import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { RichText } from '@/components/richText/RichText';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/tabs/Tabs';
import { shouldShowCalculatorStoryFooter } from '@/helper/calculatorFooter';
import useCalculatorTabs from '@/hooks/useCalculatorTabs';
import styles from '@/storyblok/calculator/Calculators.module.scss';
import { a11yProps } from '@/storyblok/tabs/Tabs';
import type { PartialCalculatorTabsStoryblok } from '@/types/component-types-sb';
import { StoryblokServerComponent } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { type FC } from 'react';

const CalculatorTabs: FC<{
  blok: PartialCalculatorTabsStoryblok;
}> = ({ blok }) => {
  const { tabs, tab, setTab } = useCalculatorTabs(blok.tabs);

  if (tabs.length === 0) {
    return null;
  }

  const activeTab = tabs.find((t) => t.uuid === tab) ?? tabs[0];
  const footerText = activeTab?.text;
  const showFooter = shouldShowCalculatorStoryFooter(footerText, {
    hideFooterText: activeTab?.hideFooterText,
  });

  if (tabs.length === 1) {
    return (
      <div>
        <div className={styles.calculatorCard} data-calculator-count={1}>
          <StoryblokServerComponent
            hideFooter={true}
            blok={tabs[0]?.content}
            calculatorStoryId={tabs[0] ? String(tabs[0].id) : undefined}
          />
        </div>
        {shouldShowCalculatorStoryFooter(tabs[0]?.text, { hideFooterText: tabs[0]?.hideFooterText }) && (
          <div className={styles.calculatorFooter}>
            <RichText text={tabs[0]?.text} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        className={classNames(styles.calculatorCard, styles.hasTabs)}
        data-testid="calculator-tabs"
        data-calculator-count={tabs.length}
      >
        <Tabs
          className={styles.calculatorTabs}
          value={tab}
          onValueChange={(value) => {
            if (value != null) {
              setTab(String(value));
            }
          }}
        >
          <TabsList scrollable variant="card">
            {tabs.map((t) => (
              <TabsTrigger key={t.uuid} value={t.uuid} {...a11yProps(t.uuid)}>
                <div className={styles.tabIcon}>
                  <StoryblokImage
                    image={t.icon}
                    ariaHidden={true}
                    aspectRatio={1}
                    width={{ pxMobile: 24, pxTablet: 24, pxDesktop: 24 }}
                  />
                </div>
                {t.tabTitle}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent
              key={t.uuid}
              value={t.uuid}
              id={`tab-panel-${t.uuid}`}
              data-testid={`tab-panel-${t.uuid}`}
              aria-labelledby={`tab-${t.uuid}`}
              className={styles.calculatorPanel}
              keepMounted
            >
              <StoryblokServerComponent hideFooter={true} blok={t.content} calculatorStoryId={String(t.id)} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
      {showFooter && (
        <div className={styles.calculatorFooter}>
          <RichText text={footerText} />
        </div>
      )}
    </div>
  );
};

export default CalculatorTabs;
